import type { QuizEditionSourceMaterial } from './quizSourceMaterial';
import {
  isIntegratorApiSourceSnapshot,
  type QuizSourceConfig,
  type QuizSourceSnapshot,
} from './quizSource';
import type { RefreshPolicy } from './refreshPolicy';
import type { LegacySeriesRefreshCompatibility } from './legacySeriesRefreshCompatibility';
import type { QuizDeliverySetup } from './quizDeliveryConfig';

/** Durable Quiz id — `public.quizzes.id`. */
export type QuizId = string;

/** Quiz Edition id — `public.quiz_editions.id`. */
export type QuizEditionId = string;

export type QuizEditionApprovalStatus = 'approved' | 'unapproved';

/** Pointer-derived edition role; not stored as an authoritative lifecycle status. */
export type QuizEditionRole = 'live' | 'draft' | 'past';

export interface QuizEditionPointers {
  liveEditionId: QuizEditionId | null;
  draftEditionId: QuizEditionId | null;
}

/** Channel availability and configuration flags from workspace read model. */
export interface QuizChannelsSummary {
  hostedLinkAvailable: boolean;
  websiteEmbedAvailable: boolean;
  emailAvailable: boolean;
  hostedLinkConfigured: boolean;
  websiteEmbedConfigured: boolean;
  emailConfigured: boolean;
  configuredCount: number;
}

export interface QuizEditionItemComposition {
  totalCount: number;
  triviaCount: number;
  pollCount: number;
}

export interface QuizEditionSummary {
  id: QuizEditionId;
  role: QuizEditionRole;
  approvalStatus: QuizEditionApprovalStatus;
  approvedAt: string | null;
  scheduledFor: string | null;
  publishedAt: string | null;
  supersededAt: string | null;
  cancelledAt: string | null;
  sourceSnapshot: QuizSourceSnapshot | null;
  sourceMaterials: QuizEditionSourceMaterial[];
  itemComposition: QuizEditionItemComposition | null;
  /** Saved admin bulk candidates, independent of selection for publishing or Upload. */
  bulkReviewComposition?: QuizEditionItemComposition | null;
  createdAt: string;
}

export interface QuizScheduledGenerationInput {
  sourceType: 'partner_rss_feed' | 'partner_url_document';
  sourceConfig: Record<string, unknown>;
  generationConfig: Record<string, unknown>;
  /** True when the saved manual source revision already produced a Quiz Edition. */
  contentRefreshRequired: boolean;
}

export function buildQuizEditionSummary(
  overrides: Partial<QuizEditionSummary> & Pick<QuizEditionSummary, 'id' | 'role'>
): QuizEditionSummary {
  return {
    approvalStatus: 'unapproved',
    approvedAt: null,
    scheduledFor: null,
    publishedAt: null,
    supersededAt: null,
    cancelledAt: null,
    sourceSnapshot: null,
    sourceMaterials: [],
    itemComposition: null,
    createdAt: '2026-06-01T00:00:00.000Z',
    ...overrides,
  };
}

export function quizEditionHasPlayableItems(
  edition: QuizEditionSummary | null | undefined
): boolean {
  if (!edition) return false;
  if (edition.itemComposition) return edition.itemComposition.totalCount > 0;

  // A Live pointer is assigned only after the database validates playable content.
  // Draft pointers are staging targets and may legitimately remain empty after a failed launch.
  return edition.role === 'live';
}

export interface QuizWorkspaceView {
  quizId: QuizId;
  partnerId: string | null;
  title: string;
  slug: string;
  liveEdition: QuizEditionSummary | null;
  draftEdition: QuizEditionSummary | null;
  pastEditions: QuizEditionSummary[];
  sourceConfig: QuizSourceConfig | null;
  refreshPolicy: RefreshPolicy;
  deliverySetup: QuizDeliverySetup;
  scheduledGenerationInput: QuizScheduledGenerationInput | null;
  /** Internal migration compatibility for refresh automation; not part of the public refresh contract. */
  legacySeriesRefreshCompatibility?: LegacySeriesRefreshCompatibility;
  channelsSummary: QuizChannelsSummary;
}

export function quizWorkspaceHasIntegratorApiDraft(
  workspace: Pick<QuizWorkspaceView, 'draftEdition'> | null | undefined
): boolean {
  return isIntegratorApiSourceSnapshot(workspace?.draftEdition?.sourceSnapshot);
}

export interface QuizSourceContentEdition {
  id: QuizEditionId;
  role: Extract<QuizEditionRole, 'live' | 'draft'>;
  sourceSnapshot: QuizSourceSnapshot | null;
  sourceMaterials: QuizEditionSourceMaterial[];
  createdAt: string;
}

export interface QuizSourceContentView {
  quizId: QuizId;
  sourceConfig: QuizSourceConfig | null;
  scheduledGenerationInput: QuizScheduledGenerationInput | null;
  liveEdition: QuizSourceContentEdition | null;
  draftEdition: QuizSourceContentEdition | null;
}
